import React, { useState, useEffect } from 'react';
import { addIncidentNote, subscribeToIncidentNotes } from '../firebase/firestoreService';
import { getCurrentUser } from '../firebase/authService';
import { formatDate } from '../utils/dateUtils';
import { getAllServiceTypes, getServiceType } from '../utils/roles';
import '../styles/IncidentNotes.css';
import { readOnlyProps } from '../utils/viewAs';

const SERVICE_OPTIONS = [
  { id: 'general', label: 'General / All-day' },
  ...getAllServiceTypes().map(s => ({ id: s.id, label: s.name }))
];

const getServiceLabel = (id) => {
  if (id === 'general') return 'General';
  const s = getServiceType(id);
  return s ? s.name : id;
};

const toDateStr = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const IncidentNotes = ({ selectedDate }) => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [serviceType, setServiceType] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [confirmingNoIncident, setConfirmingNoIncident] = useState(false);
  const dateStr = toDateStr(selectedDate);
  const user = getCurrentUser();

  useEffect(() => {
    const unsub = subscribeToIncidentNotes(dateStr, setNotes);
    return () => unsub && unsub();
  }, [dateStr]);

  const submitNote = async (text) => {
    if (!user) {
      setError('You must be signed in to add a note.');
      return false;
    }
    try {
      setSubmitting(true);
      setError('');
      await addIncidentNote({
        serviceDate: dateStr,
        serviceType,
        noteText: text,
        createdBy: user.uid,
        createdByName: user.displayName || user.email || 'Unknown'
      });
      return true;
    } catch (err) {
      console.error('Failed to add incident note:', err);
      setError('Could not save note. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = noteText.trim();
    if (!trimmed) {
      setError('Please enter a note before submitting.');
      return;
    }
    const ok = await submitNote(trimmed);
    if (ok) setNoteText('');
  };

  const handleNoIncidentConfirm = async () => {
    const ok = await submitNote('✅ No incident occurred during this service.');
    if (ok) setConfirmingNoIncident(false);
  };

  const isToday = dateStr === toDateStr(new Date());

  return (
    <div className="incident-notes-section">
      <div className="incident-notes-card">
        <button
          type="button"
          className="incident-notes-header"
          aria-expanded={expanded}
          onClick={() => setExpanded(prev => !prev)}
        >
          <div>
            <h3 className="incident-notes-title">📋 Incident & Service Notes</h3>
            <p className="incident-notes-subtitle">
              {isToday ? 'Today' : formatDate(selectedDate, 'EEEE, MMM d, yyyy')}
              {' · '}
              {notes.length} {notes.length === 1 ? 'entry' : 'entries'}
            </p>
          </div>
          <span className={`incident-notes-chevron${expanded ? ' open' : ''}`} aria-hidden="true">▼</span>
        </button>

        {expanded && (
          <div className="incident-notes-body">
            <form className="incident-notes-form" onSubmit={handleSubmit}>
              <div className="incident-notes-form-row">
                <label className="incident-notes-field" htmlFor="incident-service-select">
                  <span className="incident-notes-field-label">Service</span>
                  <select
                    id="incident-service-select"
                    className="incident-notes-select"
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    disabled={submitting || confirmingNoIncident}
                  >
                    {SERVICE_OPTIONS.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              {confirmingNoIncident ? (
                <div className="incident-no-incident-confirm" role="alertdialog" aria-live="polite">
                  <p className="incident-no-incident-confirm-text">
                    Confirm: <strong>No incident occurred</strong> during the
                    {' '}<strong>{getServiceLabel(serviceType)}</strong> service on
                    {' '}<strong>{formatDate(selectedDate, 'MMM d, yyyy')}</strong>?
                  </p>
                  <p className="incident-no-incident-confirm-hint">
                    This will be logged with your name and the current time and
                    cannot be edited or deleted.
                  </p>
                  <div className="incident-no-incident-confirm-actions">
                    <button
                      type="button"
                      className="btn-no-incident-cancel"
                      onClick={() => setConfirmingNoIncident(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-no-incident-confirm"
                      onClick={handleNoIncidentConfirm}
                      disabled={submitting || ro.disabled}
                      title={ro.title}
                    >
                      {submitting ? 'Logging…' : 'Yes, confirm no incident'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-no-incident"
                  onClick={() => {
                    setError('');
                    setConfirmingNoIncident(true);
                  }}
                  disabled={submitting || ro.disabled}
                  title={ro.title}
                >
                  ✓ No Incident Occurred
                </button>
              )}
              <textarea
                className="incident-notes-textarea"
                placeholder="Describe the incident or note (e.g., medical, lost item, parent reunification, behavioral issue)…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={3}
                disabled={submitting || ro.disabled}
                title={ro.title}
              />
              {error && <div className="incident-notes-error">{error}</div>}
              <div className="incident-notes-form-actions">
                <span className="incident-notes-author-hint">
                  Will be logged as <strong>{user?.displayName || user?.email || 'you'}</strong> at the current time
                </span>
                <button
                  type="submit"
                  className="btn-add-incident-note"
                  disabled={submitting || !noteText.trim() || ro.disabled}
                  title={ro.title}
                >
                  {submitting ? 'Adding…' : 'Add Note'}
                </button>
              </div>
            </form>

            <div className="incident-notes-list">
              {notes.length === 0 ? (
                <p className="incident-notes-empty">No incident notes recorded for this day.</p>
              ) : (
                notes.map((note) => {
                  const isNoIncident = note.noteText?.startsWith('✅ No incident');
                  return (
                    <div
                      key={note.id}
                      className={`incident-note-item${isNoIncident ? ' no-incident' : ''}`}
                    >
                      <div className="incident-note-row-top">
                        <span className={`incident-note-tag tag-${note.serviceType || 'general'}`}>
                          {getServiceLabel(note.serviceType)}
                        </span>
                        {isNoIncident && (
                          <span className="incident-note-clearance-tag">All clear</span>
                        )}
                        <span className="incident-note-time">
                          {note.createdAt ? formatDate(note.createdAt, 'MMM d, h:mm a') : '—'}
                        </span>
                      </div>
                      <p className="incident-note-text">{note.noteText}</p>
                      <span className="incident-note-author">
                        Logged by {note.createdByName || 'Unknown'}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentNotes;
